/** @format */

import {
  CompareItemAttribute,
  CompareProductDetails,
  CompareProductItem,
} from '@shared/data-access/interfaces';
import { Injectable, inject } from '@angular/core';
import { Observable, take } from 'rxjs';

import { CompareDao } from './compare.dao';

@Injectable({
  providedIn: 'root',
})
export class CompareService {
  private readonly compareDao = inject(CompareDao);

  getProductDetails(ecodes: string[], isClearance: boolean): Observable<CompareProductDetails> {
    return this.compareDao.getProductDetails(ecodes, isClearance).pipe(take(1));
  }

  coalesceAllProductAttributes(products: CompareProductItem[]): CompareProductItem[] {
    // Get all unique and sorted defining and descriptive attributes across all products
    const allSortedAttributesList = this.getAllUniqueAttributes(products);

    return products.map((product) => {
      // Extract the product's original defining attributes (or an empty array if missing)
      const originalDefining = product.style?.definingAttributes ?? [];

      // Extract only the facetable descriptive attributes from the product
      const originalDescriptive =
        product.style?.descriptiveAttributes.filter((attr) => attr.isFacetable) ?? [];

      // Coalesce the product's defining attributes against the full sorted list
      const coalescedDefining = this.coalesceAttributes(
        allSortedAttributesList.defining,
        originalDefining,
      );

      // Coalesce the product's descriptive attributes against the full sorted list
      const coalescedDescriptive = this.coalesceAttributes(
        allSortedAttributesList.descriptive,
        originalDescriptive,
      );

      // Return a new product object with updated, coalesced attributes
      return {
        ...product,
        style: {
          ...product.style,
          definingAttributes: coalescedDefining,
          descriptiveAttributes: coalescedDescriptive,
        },
      };
    });
  }

  private coalesceAttributes(
    allSortedAttributes: CompareItemAttribute[],
    originalAttributes: CompareItemAttribute[],
  ): CompareItemAttribute[] {
    // Create a map of original attributes for quick lookup by name
    const originalMap = new Map(originalAttributes.map((attr) => [attr.name, attr]));

    return allSortedAttributes.map((attr) => {
      const matched = originalMap.get(attr.name);
      // Start with the base attribute and Override with matched attribute if found Ensure value is a trimmed string or fallback to '-'
      return {
        // Start with the base attribute from allSortedAttributes
        ...attr,
        // Override with matched attribute if found
        ...(matched ?? {}),
        // If a match is found use its value, else default ('-')
        value: matched?.value?.toString().trim() || '-',
      };
    });
  }

  private getAllUniqueAttributes(products: CompareProductItem[]): {
    defining: CompareItemAttribute[];
    descriptive: CompareItemAttribute[];
  } {
    const definingAttrsMap = new Map<string, CompareItemAttribute>();
    const descriptiveAttrsMap = new Map<string, CompareItemAttribute>();

    for (const { style } of products) {
      // Add unique defining attributes
      style?.definingAttributes?.forEach((attr) => {
        if (!definingAttrsMap.has(attr.name)) {
          definingAttrsMap.set(attr.name, attr);
        }
      });

      // Add unique descriptive attributes that are facetable
      style?.descriptiveAttributes?.forEach((attr) => {
        if (attr.isFacetable && !descriptiveAttrsMap.has(attr.name)) {
          descriptiveAttrsMap.set(attr.name, attr);
        }
      });
    }

    // Sorting function to order attributes by sequence, pushing undefined to the end
    const sortBySequence = (a: CompareItemAttribute, b: CompareItemAttribute) =>
      (a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER);

    // Return sorted arrays of unique defining and descriptive attributes
    return {
      defining: [...definingAttrsMap.values()].sort(sortBySequence),
      descriptive: [...descriptiveAttrsMap.values()].sort(sortBySequence),
    };
  }
}
